import React, { useRef, useState } from "react";
import classNames from "classnames";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  useListsRetrieval,
  useRetrieveKanban,
  useTaskStatusEdition,
} from "@planzone/view-models";
import {
  id,
  task,
  Column as C,
  Status,
  Identifiable,
  column,
} from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";

import { VisibilitySwitch } from "@common/visibilitySwitch";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { Props, Sorts, Filter } from "./types";
import { StatusCreationForm } from "./statusCreationForm";
import { Loader } from "./loader";
import { Toolbar } from "./toolbar";
import { applySort, sortInitialState, SortContext } from "./utils/sort";
import { applyFilter, filterInitialState, FilterContext } from "./utils/filter";
import { Forms } from "./forms";
import { CollapsedColumn } from "./collapsedColumn";
import { Column } from "./column";
import styles from "./styles.scss";

function KanbanPage({ project }: Props) {
  const { t } = useTranslation();
  const listFilter = { limit: 999999999999, start: 0 };
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    kanban,
    retrieveKanban,
  } = useRetrieveKanban(project);
  const {
    lists,
    isRequestPending: isListRequestPending,
    retrieveLists,
  } = useListsRetrieval(project, listFilter);
  const [allocationMenuState, setAllocationMenuState] = React.useState({
    isDisplayed: false,
    x: 0,
    y: 0,
    task: { ...task, uuid: id },
  });
  const [tagMenuState, setTagMenuState] = React.useState({
    isDisplayed: false,
    x: 0,
    y: 0,
    task: { ...task, uuid: id },
  });
  const [filter, setFilter] = useState<Filter>(filterInitialState);
  const [sort, setSort] = useState<Sorts>(sortInitialState);
  const sortedKanban = applyFilter(applySort(kanban, sort), filter);
  const { editTaskStatus } = useTaskStatusEdition();
  const { pushView } = useModal();
  const isBasic = (status: Identifiable<Status>) =>
    status.label.slice(0, "pr_".length) === "pr_";
  const sortKanbanColumns = (current: C, next: C) => {
    const currentKeyRank = current.status.keyRank
      ? current.status.keyRank
      : current.status.percentComplete === 100
        ? 100
        : 0;
    const nextKeyRank = next.status.keyRank
      ? next.status.keyRank
      : next.status.percentComplete === 100
        ? 100
        : 0;
    return currentKeyRank > nextKeyRank
      ? 1
      : currentKeyRank < nextKeyRank
        ? -1
        : 0;
  };
  const sortedColumns = [...sortedKanban.columns].sort(sortKanbanColumns);
  const moveColumn = React.useCallback(
    (column: C, { min, max }: { min?: C; max?: C }) => {
      const minKeyRank = !min
        ? 0
        : min.status.keyRank
          ? min.status.keyRank
          : min.status.label === "pr_done"
            ? 100
            : 0;
      const maxKeyRank = !max
        ? 0
        : max.status.keyRank
          ? max.status.keyRank
          : max.status.label === "pr_done"
            ? 100
            : 0;

      editTaskStatus({
        status: column.status,
        nextStatus: {
          ...column.status,
          keyRank:
            min && max
              ? (minKeyRank + maxKeyRank) / 2
              : min && !max
                ? minKeyRank + 1
                : !min && max
                  ? maxKeyRank - 1
                  : 0,
        },
      });
    },
    [editTaskStatus],
  );

  React.useEffect(() => {
    !!project.uuid &&
      !isListRequestPending &&
      retrieveLists({
        project,
        filter: listFilter,
      });
  }, [project.uuid]);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveKanban(project);
  }, [
    project.uuid,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveKanban,
  ]);

  return (
    <DragAndDrop>
      <FilterContext.Provider value={{ filter, setFilter }}>
        <SortContext.Provider value={{ sort, setSort }}>
          <div className={styles.kanbanPage}>
            <Helmet>
              <title>Planzone • {t("kanban.title")}</title>
              <meta name="description" content={project.description} />
            </Helmet>
            <div className={styles.filters}>
              <Toolbar />
            </div>
            <div className={styles.columns}>
              {isRequestPending ? (
                <Loader />
              ) : (
                <React.Fragment>
                  <DropBox
                    className={classNames(
                      styles.collapseColumn,
                      styles.dropBox,
                    )}
                    acceptedTypes={["MOVE_COLUMN"]}
                    horizontalSlop={50}
                    drop={({ payload }) =>
                      moveColumn(payload.column, { max: sortedColumns[0] })
                    }
                  >
                    {({ payload }, canDrop) => (
                      <div
                        className={classNames(styles.dropZone, {
                          [styles.hideColumnContent]: canDrop,
                        })}
                      >
                        {canDrop && (
                          <Column
                            column={payload.column}
                            filter={filter}
                            project={project}
                            rootList={lists}
                            collapse={() => {}}
                            removeColumn={() => {}}
                            displayAllocationMenu={() => {}}
                            displayTagMenu={() => {}}
                          />
                        )}
                      </div>
                    )}
                  </DropBox>
                  {sortedColumns.map((column, index) => (
                    <div key={column.status.uuid} className={styles.column}>
                      <VisibilitySwitch
                        renderChildren={(isCollapsed, collapse, expand) =>
                          isCollapsed ? (
                            <CollapsedColumn
                              status={column.status}
                              expand={expand}
                            />
                          ) : (
                            <React.Fragment>
                              <Draggable
                                key={column.status.uuid}
                                data={{ column }}
                                type="MOVE_COLUMN"
                                draggedClassName={styles.dragged}
                                isDisabled={isBasic(column.status)}
                              >
                                <Column
                                  column={column}
                                  filter={filter}
                                  project={project}
                                  rootList={lists}
                                  collapse={collapse}
                                  removeColumn={(col) => {
                                    const c = kanban.columns.find(
                                      (c) => c.status.uuid !== col.status.uuid,
                                    );

                                    pushView({
                                      key: UseCases.removeTaskStatus,
                                      data: {
                                        status: col.status,
                                        project,
                                        hasNoTasks: column.tasks.length === 0,
                                      },
                                    });
                                  }}
                                  displayAllocationMenu={(task, x, y) =>
                                    setAllocationMenuState({
                                      isDisplayed: true,
                                      x,
                                      y,
                                      task,
                                    })
                                  }
                                  displayTagMenu={(task, x, y) =>
                                    setTagMenuState({
                                      isDisplayed: true,
                                      x,
                                      y,
                                      task,
                                    })
                                  }
                                />
                              </Draggable>
                              <DropBox
                                className={classNames(
                                  styles.collapseColumn,
                                  styles.dropBox,
                                )}
                                acceptedTypes={["MOVE_COLUMN"]}
                                horizontalSlop={50}
                                drop={({ payload }) =>
                                  moveColumn(payload.column, {
                                    min: sortedColumns.find(
                                      (c) =>
                                        c.status.uuid === column.status.uuid,
                                    ),
                                    max: sortedColumns[index + 1],
                                  })
                                }
                              >
                                {({ payload }, canDrop) => (
                                  <div
                                    className={classNames(styles.dropZone, {
                                      [styles.hideColumnContent]: canDrop,
                                    })}
                                  >
                                    {canDrop && (
                                      <Column
                                        column={payload.column}
                                        filter={filter}
                                        project={project}
                                        rootList={lists}
                                        collapse={() => {}}
                                        removeColumn={() => {}}
                                        displayAllocationMenu={() => {}}
                                        displayTagMenu={() => {}}
                                      />
                                    )}
                                  </div>
                                )}
                              </DropBox>
                            </React.Fragment>
                          )
                        }
                      />
                    </div>
                  ))}
                </React.Fragment>
              )}
              <div className={styles.statusCreationForm}>
                <StatusCreationForm project={project} />
              </div>
            </div>
            <Forms
              project={project}
              allocationMenuState={allocationMenuState}
              tagMenuState={tagMenuState}
              setAllocationMenuState={setAllocationMenuState}
              setTagMenuState={setTagMenuState}
            />
          </div>
        </SortContext.Provider>
      </FilterContext.Provider>
    </DragAndDrop>
  );
}

export { KanbanPage };
